import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { BoardCard } from './deskBoard.js';

/**
 * Levar a pessoa até um card do quadro — o "Mostrar no quadro" da busca e do
 * aviso de que o card foi movido. É o `SearchScrollTargetContext` do legado
 * (`App.tsx:94-99`, `:5122-5170`).
 *
 * A coluna do card — a que o SERVIDOR calculou, `card.column` — se abre
 * sozinha, rola para a vista e mostra o card com destaque. Se ele ainda não
 * está entre os carregados (a coluna pagina de 30 em 30, e a 31+ passa de mil
 * na produção), ele aparece no topo dela, marcado como trazido até ali, em vez
 * de a tela baixar página atrás de página até achá-lo.
 */
export interface DeskFocusTarget {
  card: BoardCard;
  /** Muda a cada pedido — pedir o mesmo card duas vezes rola de novo. */
  nonce: number;
}

interface DeskFocusValue {
  target: DeskFocusTarget | null;
  show: (card: BoardCard) => void;
  clear: () => void;
}

const DeskFocusContext = createContext<DeskFocusValue>({
  target: null,
  show: () => undefined,
  clear: () => undefined,
});

export function DeskFocusProvider({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<DeskFocusTarget | null>(null);
  const show = useCallback((card: BoardCard) => setTarget({ card, nonce: Date.now() }), []);
  const clear = useCallback(() => setTarget(null), []);
  const value = useMemo(() => ({ target, show, clear }), [target, show, clear]);
  return <DeskFocusContext.Provider value={value}>{children}</DeskFocusContext.Provider>;
}

export function useDeskFocus(): DeskFocusValue {
  return useContext(DeskFocusContext);
}

/** O `id` do card no DOM, com a base junto — o mesmo quadro mostra as duas. */
export function cardDomId(tenant: string, id: number): string {
  return `mesa-card-${tenant}-${id}`;
}

/**
 * A coluna do quadro que mostra a chave `key` (de `deskColumnKey`), ou `null`
 * quando esta pessoa não a vê.
 *
 * Por atributo, e não por `id`: uma coluna pode juntar várias chaves — a fila
 * mãe de vencimento junta as seis faixas do dia do mês —, e cada uma leva a
 * lista em `data-desk-keys`.
 */
export function findColumn(key: string): Element | null {
  return document.querySelector(`[data-desk-keys~="${key.replace(/["\\]/g, '\\$&')}"]`);
}
